import axios from 'axios';
import { Project, CreateProjectRequest, Document, UploadResponse, OrdinanceTypesResponse, ExtractionJobStatus, ExtractionStartResponse } from '../types/project';
import { API_BASE_URL } from '../config/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses (expired/invalid token)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid, clear it and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const projectsApi = {
  // Get all projects
  getProjects: async (): Promise<Project[]> => {
    const response = await apiClient.get('/projects/');
    return response.data;
  },

  // Get project by ID
  getProject: async (projectId: string): Promise<Project> => {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  },

  // Create new project
  createProject: async (request: CreateProjectRequest): Promise<Project> => {
    const response = await apiClient.post('/projects/', request);
    return response.data;
  },

  // Delete project
  deleteProject: async (projectId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}`);
  },

  // Get available ordinance types
  getOrdinanceTypes: async (): Promise<string[]> => {
    const response = await apiClient.get<OrdinanceTypesResponse>('/config/ordinance-types');
    return response.data.ordinance_types;
  },

  // Upload documents to project
  uploadDocuments: async (projectId: string, files: File[]): Promise<UploadResponse[]> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await apiClient.post(`/projects/${projectId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Get project documents
  getProjectDocuments: async (projectId: string): Promise<Document[]> => {
    const response = await apiClient.get(`/projects/${projectId}/documents`);
    return response.data;
  },

  // Delete a document
  deleteDocument: async (projectId: string, documentId: string): Promise<{ message: string; document_id: string; document_name: string }> => {
    const response = await apiClient.delete(`/projects/${projectId}/documents/${documentId}`);
    return response.data;
  },

  // Get requirements count for a document (for delete confirmation)
  getDocumentRequirementsCount: async (projectId: string, documentId: string): Promise<{
    document_id: string;
    document_name: string;
    requirement_count: number;
    sample_requirement_titles: string[];
    ai_processed: boolean;
  }> => {
    const response = await apiClient.get(`/projects/${projectId}/documents/${documentId}/requirements-count`);
    return response.data;
  },

  // AI Requirements Extraction (async)
  startExtraction: async (projectId: string): Promise<ExtractionStartResponse> => {
    const response = await apiClient.post(`/projects/${projectId}/extract-requirements`);
    return response.data;
  },

  // Get job status by job ID
  getJobStatus: async (jobId: string): Promise<ExtractionJobStatus> => {
    const response = await apiClient.get(`/jobs/${jobId}/status`);
    return response.data;
  },

  getExtractionStatus: async (projectId: string): Promise<any> => {
    const response = await apiClient.get(`/projects/${projectId}/extraction-status`);
    return response.data;
  },

  // Get saved requirements for a project with pagination and filtering
  getRequirements: async (
    projectId: string,
    page: number = 1,
    limit: number = 20,
    statusFilter: string = 'all',
    categoryFilter: string = 'all',
    requirementTypeFilter: string = 'all',
    zoneFilter: string = 'all',
    searchTerm: string = '',
    ordinanceTypeFilter: string = 'all',
    ordinanceHierarchyFilter: string = 'all'
  ): Promise<any> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      status_filter: statusFilter,
      category_filter: categoryFilter,
      requirement_type_filter: requirementTypeFilter,
      zone_filter: zoneFilter,
      ordinance_type_filter: ordinanceTypeFilter,
      ordinance_hierarchy_filter: ordinanceHierarchyFilter
    });

    // Only add search parameter if there's a search term
    if (searchTerm && searchTerm.trim()) {
      params.set('search', searchTerm.trim());
    }

    const fullUrl = `${API_BASE_URL}/projects/${projectId}/requirements?${params}`;
    console.log('🌐 Frontend API call:', {
      projectId,
      page,
      limit,
      statusFilter,
      categoryFilter,
      requirementTypeFilter,
      zoneFilter,
      searchTerm,
      fullUrl
    });

    const response = await apiClient.get(`/projects/${projectId}/requirements?${params}`);
    console.log('📥 Frontend API response:', {
      status: response.status,
      dataKeys: Object.keys(response.data),
      requirementsCount: response.data.requirements?.length,
      totalCount: response.data.pagination?.total_count,
      searchTerm,
      fullResponse: response.data
    });

    return response.data;
  },

  // Get zoning districts for a project
  getZoningDistricts: async (projectId: string): Promise<any> => {
    const response = await apiClient.get(`/projects/${projectId}/zoning-districts`);
    return response.data;
  },

  // Get document processing costs
  getDocumentCosts: async (projectId: string): Promise<any> => {
    const response = await apiClient.get(`/projects/${projectId}/document-costs`);
    return response.data;
  },

  // Upload submission documents to project
  uploadSubmissionDocuments: async (projectId: string, files: File[]): Promise<UploadResponse[]> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await apiClient.post(`/projects/${projectId}/upload-submission`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Get project submission documents
  getSubmissionDocuments: async (projectId: string): Promise<Document[]> => {
    const response = await apiClient.get(`/projects/${projectId}/submission-documents`);
    return response.data;
  },

  // Compliance Evaluation APIs (Phase 1A)
  startComplianceEvaluation: async (
    projectId: string,
    submissionDocIds?: string[]
  ): Promise<ExtractionStartResponse> => {
    const response = await apiClient.post(`/projects/${projectId}/evaluate-compliance`, {
      submission_doc_ids: submissionDocIds
    });
    return response.data; // Returns { job_id, message } like existing extraction
  },

  startComplianceRerun: async (
    projectId: string,
    submissionDocIds: string[],
    rerunStatuses: string[]
  ): Promise<ExtractionStartResponse> => {
    const response = await apiClient.post(`/projects/${projectId}/rerun-compliance-evaluation`, {
      submission_doc_ids: submissionDocIds,
      rerun_statuses: rerunStatuses,
      skip_prefiltering: true  // Skip Stage 0 - start rerun at Stage 1 for detailed analysis
    });
    return response.data; // Returns { job_id, message } like existing extraction
  },

  getComplianceResults: async (projectId: string, jobId: string) => {
    const response = await apiClient.get(`/projects/${projectId}/compliance-results/${jobId}`);
    return response.data;
  },

  // Update requirement approval status
  updateRequirementApproval: async (projectId: string, requirementId: string, approved: boolean) => {
    const response = await apiClient.put(`/projects/${projectId}/requirements/${requirementId}/approval`, {
      approved
    });
    return response.data;
  },

  // Bulk update requirement approval status
  bulkUpdateRequirementApproval: async (
    projectId: string,
    approved: boolean,
    options?: {
      requirement_ids?: string[];
      zoning_districts?: string[];
      requirement_types?: string[];
    }
  ) => {
    const response = await apiClient.put(`/projects/${projectId}/requirements/bulk-approval`, {
      requirement_ids: options?.requirement_ids || [],
      approved,
      zoning_districts: options?.zoning_districts || [],
      requirement_types: options?.requirement_types || []
    });
    return response.data;
  },

  // Get compliance evaluation jobs for a project
  getComplianceJobs: async (projectId: string) => {
    const response = await apiClient.get(`/projects/${projectId}/compliance-jobs`);
    return response.data;
  },

  // Submission document deletion endpoints
  getSubmissionDocumentComplianceImpact: async (projectId: string, documentId: string) => {
    const response = await apiClient.get(`/projects/${projectId}/submission-documents/${documentId}/compliance-impact`);
    return response.data;
  },

  deleteSubmissionDocument: async (projectId: string, documentId: string) => {
    const response = await apiClient.delete(`/projects/${projectId}/submission-documents/${documentId}`);
    return response.data;
  },

  // Update requirement compliance status
  updateRequirementCompliance: async (projectId: string, requirementId: string, compliance_status: string, compliance_notes?: string) => {
    const response = await apiClient.put(`/projects/${projectId}/requirements/${requirementId}/compliance`, {
      compliance_status,
      compliance_notes
    });
    return response.data;
  },

  // Generate and download compliance report PDF
  generateComplianceReport: async (projectId: string): Promise<Blob> => {
    const response = await apiClient.post(`/projects/${projectId}/compliance-report`, {}, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    return response.data;
  },

  // Export approved requirements to CSV
  exportApprovedRequirementsCSV: async (projectId: string): Promise<Blob> => {
    const response = await apiClient.get(`/projects/${projectId}/requirements/export-csv`, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    });
    return response.data;
  },
};

export default apiClient;